import React from "react";

const STREAK_KEY = "ba_cp_lesson_streak_v2";
const PENDING_KEY = "ba_cp_hallway_celebrate_pending_v1";

export const MIN_STREAK_FOR_HALLWAY_MODAL = 3;

type HallwayCelebrationData = {
	lessonCount: number;
	starTotal: number;
};

type NavButtonProps = {
	buttonText: string;
	buttonType: string;
	summaryNavChoice: string;
	linkTo?: string;
	handleClick?: () => void;
	leftContent?: React.JSX.Element;
};

type PendingHallwayCelebrationData = {
	styleType: string;
	gradeNumber: number;
	chapterIndex: number;
	totalStarsEarnedInStreak: number;
	nextLessonButtonProps: NavButtonProps;
};

let memoryStreakState: HallwayCelebrationData = {lessonCount: 0, starTotal: 0};
let memoryPendingHallway: PendingHallwayCelebrationData | null = null;

function getSessionStorage() {
	return typeof global.sessionStorage !== "undefined"
		? global.sessionStorage
		: null;
}

function readState(): HallwayCelebrationData {
	const sessonStorage = getSessionStorage();
	if (!sessonStorage) {
		return {...memoryStreakState};
	}
	try {
		const raw = sessonStorage.getItem(STREAK_KEY);
		if (!raw) {
			return {lessonCount: 0, starTotal: 0};
		}
		const parsed = JSON.parse(raw) as HallwayCelebrationData;
		const lessonCount =
			typeof parsed.lessonCount === "number" && parsed.lessonCount >= 0
				? parsed.lessonCount
				: 0;
		const starTotal =
			typeof parsed.starTotal === "number" && parsed.starTotal >= 0
				? parsed.starTotal
				: 0;
		return {lessonCount, starTotal};
	} catch (e) {
		return {lessonCount: 0, starTotal: 0};
	}
}

function writeState(state: HallwayCelebrationData) {
	const sessionStorage = getSessionStorage();
	if (!sessionStorage) {
		memoryStreakState = {...state};
		return;
	}
	sessionStorage.setItem(STREAK_KEY, JSON.stringify({...state}));
}

export function recordCompletedLessonWithStars(
	starsEarned: number | undefined
) {
	const normalizedStarsEarned =
		typeof starsEarned === "number" && starsEarned > 0
			? Math.floor(starsEarned)
			: 0;
	if (normalizedStarsEarned <= 0) {
		return;
	}
	const cur = readState();
	writeState({
		lessonCount: cur.lessonCount + 1,
		starTotal: cur.starTotal + normalizedStarsEarned,
	});
}

export function clearStreak() {
	const sessionStorage = getSessionStorage();
	if (!sessionStorage) {
		memoryStreakState = {lessonCount: 0, starTotal: 0};
		return;
	}
	sessionStorage.removeItem(STREAK_KEY);
}

export function resetStartingLessonFromHallway() {
	clearStreak();
}

export function consumeStreakAfterArrivingAtChapterFromProblem(): HallwayCelebrationData {
	const streakState = readState();
	clearStreak();
	return streakState;
}

export function setPendingHallwayCelebration(
	data: PendingHallwayCelebrationData
) {
	const sessionStorage = getSessionStorage();
	if (!sessionStorage) {
		memoryPendingHallway = data;
		return;
	}
	sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
}

export function takePendingHallwayCelebration() {
	const sessionStorage = getSessionStorage();
	if (!sessionStorage) {
		const data = memoryPendingHallway;
		memoryPendingHallway = null;
		return data;
	}
	try {
		const raw = sessionStorage.getItem(PENDING_KEY);
		if (!raw) return null;
		sessionStorage.removeItem(PENDING_KEY);
		return JSON.parse(raw);
	} catch (e) {
		sessionStorage.removeItem(PENDING_KEY);
		return null;
	}
}
